import React, { useState } from "react";
import emailjs from "@emailjs/browser";
import {
  Mail,
  Phone,
  MapPin,
  Send,
  Github,
  Linkedin,
  Twitter,
} from "lucide-react";
import { profile } from "../mock";
import { toast } from "sonner";
import { emailConfig, isContactEmailConfigured } from "../config/email";

const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [sending, setSending] = useState(false);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setSending(true);
    try {
      if (isContactEmailConfigured) {
        const payload = {
          from_name: form.name,
          from_email: form.email,
          subject: form.subject || "New portfolio message",
          message: form.message,
          timestamp: new Date().toISOString(),
          page_url: window.location.href,
        };
        await emailjs.send(
          emailConfig.serviceId,
          emailConfig.templateId,
          payload,
          emailConfig.publicKey,
        );
        toast.success("Message sent. I'll get back to you within 24 hours.");
      } else {
        // Fallback when EmailJS keys aren't set yet -store locally
        const existing = JSON.parse(
          localStorage.getItem("contact_submissions") || "[]",
        );
        existing.push({ ...form, at: new Date().toISOString() });
        localStorage.setItem("contact_submissions", JSON.stringify(existing));
        toast.success("Message saved locally. (Email not configured yet.)");
      }
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(
        "Couldn't send right now. Please try again or email me directly.",
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="contact"
      className="section-padding bg-[#0a0a0a] text-[#f4f1ea] relative grain overflow-hidden"
    >
      <div
        className="absolute -bottom-40 -left-40 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(160,29,63,0.07) 0%, transparent 70%)",
        }}
      />

      <div className="container-x relative">
        <div className="grid grid-cols-12 gap-y-14 md:gap-x-12">
          <div className="col-span-12 md:col-span-5">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#a01d3f] mb-6">
              <span className="text-[#f4f1ea]/40">[ 05 ]</span> &nbsp;—&nbsp;
              Contact
            </p>
            <h2 className="font-display text-4xl md:text-6xl font-semibold leading-[0.95] tracking-tight">
              Got an idea?
              <br />
              <span className="italic font-light bg-gradient-to-r from-[#a01d3f] via-[#d44a6a] to-[#f4f1ea] bg-clip-text text-transparent">
                Let's make it real.
              </span>
            </h2>
            <p className="mt-6 text-[#f4f1ea]/65 text-base leading-relaxed max-w-md">
              Whether it's a new product, a stuck migration, or a code review
              you've been putting off, I'd love to hear about it.
            </p>

            <div className="mt-10 space-y-5">
              <div className="flex items-center gap-4">
                <span className="w-10 h-10 rounded-full bg-[#f4f1ea]/8 flex items-center justify-center">
                  <MapPin size={16} />
                </span>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#f4f1ea]/45">
                    Based
                  </div>
                  <div className="text-sm font-medium">{profile.location}</div>
                </div>
              </div>
            </div>

            <div className="mt-10 flex gap-4">
              <a
                href={profile.socials.github}
                aria-label="GitHub"
                className="w-10 h-10 rounded-full border border-[#f4f1ea]/15 flex items-center justify-center hover:bg-[#a01d3f] hover:border-[#a01d3f] transition-colors"
              >
                <Github size={16} />
              </a>
              <a
                href={profile.socials.linkedin}
                aria-label="LinkedIn"
                className="w-10 h-10 rounded-full border border-[#f4f1ea]/15 flex items-center justify-center hover:bg-[#a01d3f] hover:border-[#a01d3f] transition-colors"
              >
                <Linkedin size={16} />
              </a>
            </div>
          </div>

          <div className="col-span-12 md:col-span-7">
            <form
              onSubmit={handleSubmit}
              className="bg-[#0f0f0f] border border-[#f4f1ea]/10 rounded-2xl p-7 md:p-10"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label
                    htmlFor="name"
                    className="block font-mono text-[10px] uppercase tracking-widest text-[#f4f1ea]/55 mb-2"
                  >
                    Your name
                  </label>
                  <input
                    id="name"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Jane Doe"
                    className="w-full bg-transparent border-b border-[#f4f1ea]/20 py-3 text-[#f4f1ea] placeholder:text-[#f4f1ea]/30 focus:outline-none focus:border-[#a01d3f] transition-colors"
                  />
                </div>
                <div>
                  <label
                    htmlFor="email"
                    className="block font-mono text-[10px] uppercase tracking-widest text-[#f4f1ea]/55 mb-2"
                  >
                    Email
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="jane@studio.com"
                    className="w-full bg-transparent border-b border-[#f4f1ea]/20 py-3 text-[#f4f1ea] placeholder:text-[#f4f1ea]/30 focus:outline-none focus:border-[#a01d3f] transition-colors"
                  />
                </div>
              </div>
              <div className="mt-5">
                <label
                  htmlFor="subject"
                  className="block font-mono text-[10px] uppercase tracking-widest text-[#f4f1ea]/55 mb-2"
                >
                  Subject
                </label>
                <input
                  id="subject"
                  name="subject"
                  value={form.subject}
                  onChange={handleChange}
                  placeholder="Product, feature, or system idea"
                  className="w-full bg-transparent border-b border-[#f4f1ea]/20 py-3 text-[#f4f1ea] placeholder:text-[#f4f1ea]/30 focus:outline-none focus:border-[#a01d3f] transition-colors"
                />
              </div>
              <div className="mt-5">
                <label
                  htmlFor="message"
                  className="block font-mono text-[10px] uppercase tracking-widest text-[#f4f1ea]/55 mb-2"
                >
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  rows={5}
                  placeholder="What are we building, and why does it matter?"
                  className="w-full bg-transparent border-b border-[#f4f1ea]/20 py-3 text-[#f4f1ea] placeholder:text-[#f4f1ea]/30 focus:outline-none focus:border-[#a01d3f] transition-colors resize-none"
                />
              </div>

              <div className="mt-8 flex items-center justify-between gap-4 flex-wrap">
                <span className="font-mono text-[10px] uppercase tracking-widest text-[#f4f1ea]/40">
                  I will get back to you really soon!
                </span>
                <button
                  type="submit"
                  disabled={sending}
                  className="btn-lime inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-medium disabled:opacity-60"
                >
                  {sending ? "Sending…" : "Send message"}
                  <Send size={15} />
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
